import { useMemo } from 'react';

import { useChecklistCategories } from '@/hooks/useChecklistCategories';
import isEligibleForPostScheduledIntake from '@/pages/Profile/components/ChecklistSection/utils/checkPSIEligibility';
import isEligibleForSelfScheduledIntake from '@/pages/Profile/components/ChecklistSection/utils/checkSSIEligibility';
import PSI_STEPS from '@/pages/Profile/components/ChecklistSection/utils/psiSteps';
import { Person } from '@/types/person';

export const IntakeStage = {
  NOT_SSI_ELIGIBLE: 'NOT_SSI_ELIGIBLE',
  SSI: 'SSI',
  PSI: 'PSI'
} as const;
export type IntakeStageValue = (typeof IntakeStage)[keyof typeof IntakeStage];

/**
 * Stage resolution rules. Order matters — earlier entries take precedence.
 *
 * Each entry colocates the membership check with the user-facing reason text,
 * so when the eligibility logic changes the reason stays in sync. If a stage
 * resolver doesn't match, the member falls through to NOT_SSI_ELIGIBLE, whose
 * reason text comes from the backend (useSSIEligibilityDetails) rather than
 * being inlined here.
 */
const STAGE_RESOLVERS: ReadonlyArray<{
  stage: Exclude<IntakeStageValue, typeof IntakeStage.NOT_SSI_ELIGIBLE>;
  matches: (person: Person) => boolean;
  reason: string;
}> = [
  {
    stage: IntakeStage.PSI,
    matches: isEligibleForPostScheduledIntake,
    reason: 'Potential member has unlocked the Get Ready Guide.'
  },
  {
    stage: IntakeStage.SSI,
    matches: isEligibleForSelfScheduledIntake,
    reason: 'Member meets all SSI eligibility criteria'
  }
];

export interface IntakeProgressItem {
  id: string;
  title: string;
  completed: boolean;
  completed_at: string | null;
  classificationKey?: string;
}

interface UseIntakeProgressResult {
  currentStage: IntakeStageValue;
  /**
   * Human-readable reason describing why the member is in `currentStage`.
   * Null for NOT_SSI_ELIGIBLE — that reason comes from the backend via
   * `useSSIEligibilityDetails` (ineligibility_reasons), not from this hook.
   */
  eligibilityReason: string | null;
  lastCompletedItem: IntakeProgressItem | null;
  lastActivityDate: string | null;
  nextItem: IntakeProgressItem | null;
  isLoading: boolean;
}

const resolveStage = (
  person: Person | null | undefined
): { stage: IntakeStageValue; reason: string | null } => {
  if (!person) return { stage: IntakeStage.NOT_SSI_ELIGIBLE, reason: null };
  const match = STAGE_RESOLVERS.find((resolver) => resolver.matches(person));
  return match
    ? { stage: match.stage, reason: match.reason }
    : { stage: IntakeStage.NOT_SSI_ELIGIBLE, reason: null };
};

const useIntakeProgress = (person: Person | null | undefined): UseIntakeProgressResult => {
  const { data: checklistData, isLoading } = useChecklistCategories(
    {
      person_id: person?.id || '',
      slug: 'self-schedule-intake'
    },
    !!person?.id
  );

  return useMemo(() => {
    const { stage: currentStage, reason: eligibilityReason } = resolveStage(person);

    const ssiItems: IntakeProgressItem[] = (checklistData?.data?.results?.[0]?.items || []).map(
      (item) => ({
        id: item.id,
        title: item.title,
        completed: item.completed,
        completed_at: item.completed_at
      })
    );

    const classificationByKey = new Map(
      (person?.active_classifications || []).map((c) => [c.key, c])
    );

    // The SSI checklist API does not expose its items' classification keys, so
    // dedup is by title — the only known overlap today ("Documents Signed").
    const ssiTitles = new Set(ssiItems.map((item) => item.title));

    const psiItems: IntakeProgressItem[] = PSI_STEPS.filter(
      (step) => !ssiTitles.has(step.title)
    ).map((step) => {
      const classification = classificationByKey.get(step.classificationKey);
      return {
        id: step.id,
        title: step.title,
        completed: !!classification,
        completed_at: classification?.active_from ?? null,
        classificationKey: step.classificationKey
      };
    });

    const mergedItems = [...ssiItems, ...psiItems];
    const completedItems = mergedItems.filter((item) => item.completed);
    const completedWithDate = completedItems
      .filter((item) => item.completed_at)
      .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime());

    const lastActivityDate = completedWithDate[0]?.completed_at ?? null;
    const lastCompletedItem = completedWithDate[0] ?? null;

    let nextItem: IntakeProgressItem | null = null;
    if (currentStage === IntakeStage.SSI) {
      nextItem = ssiItems.find((item) => !item.completed) ?? null;
    } else if (currentStage === IntakeStage.PSI) {
      const nextPsiStep = PSI_STEPS.find(
        (step) => !classificationByKey.has(step.classificationKey)
      );
      nextItem = nextPsiStep
        ? {
            id: nextPsiStep.id,
            title: nextPsiStep.title,
            completed: false,
            completed_at: null,
            classificationKey: nextPsiStep.classificationKey
          }
        : null;
    }

    return {
      currentStage,
      eligibilityReason,
      lastCompletedItem,
      lastActivityDate,
      nextItem,
      isLoading
    };
  }, [checklistData, person, isLoading]);
};

export default useIntakeProgress;
